import { Box, styled } from "@material-ui/core";
import { Fragment, memo } from "react";
import {
  EPOCUS_BORDER,
  EPOCUS_INACTIVE,
  EPOCUS_PRIMARY,
  EPOCUS_SELECTED_BG,
  rem,
} from "../../../_lib/epocus-palette";

const Block = styled(Box)({
  "& + &": { marginTop: 10 },
});

const List = styled("ul")({
  margin: 0,
  paddingLeft: 18,
  listStyle: "none",
});

const Item = styled("li")({
  position: "relative",
  "&::before": {
    content: '"·"',
    position: "absolute",
    left: -12,
    color: EPOCUS_INACTIVE,
    fontWeight: 700,
  },
  "& + &": { marginTop: 3 },
});

const Strong = styled("strong")({
  fontWeight: 700,
  color: EPOCUS_PRIMARY,
});

const Emphasis = styled("em")({
  fontWeight: 700,
  fontStyle: "normal",
  color: EPOCUS_PRIMARY,
});

const Code = styled("code")({
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
  fontSize: rem(12.5),
  letterSpacing: ".02em",
  backgroundColor: EPOCUS_SELECTED_BG,
  border: `1px solid ${EPOCUS_BORDER}`,
  borderRadius: 5,
  padding: "1px 5px",
  overflowWrap: "anywhere",
});

const INLINE = /(\*\*[^*\n]+\*\*|\*[^*\n]+\*|`[^`\n]+`)/g;

const inline = (text: string, keyPrefix: string) =>
  text
    .split(INLINE)
    .filter((chunk) => chunk !== "")
    .map((chunk, index) => {
      const key = `${keyPrefix}-${index}`;
      if (chunk.startsWith("**") && chunk.endsWith("**")) {
        return <Strong key={key}>{chunk.slice(2, -2)}</Strong>;
      }
      if (chunk.startsWith("`") && chunk.endsWith("`")) {
        return <Code key={key}>{chunk.slice(1, -1)}</Code>;
      }
      if (chunk.startsWith("*") && chunk.endsWith("*")) {
        return <Emphasis key={key}>{chunk.slice(1, -1)}</Emphasis>;
      }
      return <Fragment key={key}>{chunk}</Fragment>;
    });

type ParsedBlock =
  | { kind: "paragraph"; lines: string[] }
  | { kind: "list"; items: string[] };

const BULLET = /^\s*[-*+]\s+(.*)$/;

export const parseBlocks = (text: string): ParsedBlock[] => {
  const blocks: ParsedBlock[] = [];

  for (const line of text.split("\n")) {
    const bullet = BULLET.exec(line);
    const last = blocks.at(-1);

    if (bullet) {
      if (last?.kind === "list") last.items.push(bullet[1]);
      else blocks.push({ kind: "list", items: [bullet[1]] });
      continue;
    }
    if (line.trim() === "") {
      if (last) blocks.push({ kind: "paragraph", lines: [] });
      continue;
    }
    if (last?.kind === "paragraph") last.lines.push(line);
    else blocks.push({ kind: "paragraph", lines: [line] });
  }

  return blocks.filter(
    (block) => block.kind === "list" || block.lines.length > 0,
  );
};

export const RichText = memo<{ text: string }>(({ text }) => (
  <>
    {parseBlocks(text).map((block, index) =>
      block.kind === "list" ? (
        <Block key={`b-${index}`} component={List}>
          {block.items.map((item, itemIndex) => (
            <Item key={`i-${itemIndex}`}>{inline(item, `i-${itemIndex}`)}</Item>
          ))}
        </Block>
      ) : (
        <Block key={`b-${index}`}>
          {inline(block.lines.join("\n"), `p-${index}`)}
        </Block>
      ),
    )}
  </>
));
RichText.displayName = "RichText";
